import { AppBreadcrumb, AppCard, AppContainer } from '@/components/atoms';
import CreateMassagerForm, {
  type CreateMassagerFormData,
} from '@/pages/admin/admin-massagers/CreateMassagerForm';
import { mapDetailToFormData } from '@/pages/admin/admin-massagers/massagerFormMapper';
import { useAdminClinic } from '@/hooks/admin/useAdminClinics';
import { useAdminMassager, useAdminUpdateMassager } from '@/hooks/admin/useAdminMassagers';
import type { UpdateMassagerPayload } from '@/services/admin/massagers';
import useAppToast from '@/hooks/useAppToast';
import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';
import { dateValueToIso } from '@/utils/dateConverter';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getClinicOptions } from '@/services/admin/clinics';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { AuditInfo } from '@/components/molecules';

const EditMassagerPage: React.FC = () => {
  const toast = useAppToast();
  const navigate = useNavigate();
  const { site, id } = useParams<{ site: string; id: string }>();
  const massagerId = id ? parseInt(id, 10) : null;

  const { updateMassagerMutation, isLoading } = useAdminUpdateMassager();

  const {
    data: massagerResponse,
    isLoading: isLoadingMassager,
    isError: isMassagerError,
    error: massagerError,
  } = useAdminMassager(site ?? null, massagerId);
  const massagerData = massagerResponse?.data;

  const [selectedClinicIdForLocations, setSelectedClinicIdForLocations] = useState(
    massagerData?.clinic_id ?? 0
  );
  const clinicIdForLocations = massagerData?.clinic_id ?? selectedClinicIdForLocations;

  const siteForBreadcrumb = site ?? 'medix';

  // Fetch clinic options with latest name from clinic_informations (fallback to clinic_number)
  const { data: clinicOptionsData } = useQuery({
    queryKey: ['admin', 'clinics', 'options', site],
    queryFn: () => getClinicOptions(site ?? 'medix'),
    enabled: !!site,
  });
  const { data: singleClinicData } = useAdminClinic(site ?? '', clinicIdForLocations);

  const clinicOptions = useMemo(() => {
    const list = clinicOptionsData ?? [];
    // For 治療院ID select, show latest clinic_information.name if exists, else clinic_number
    return list.map(c => ({
      id: c.id,
      clinic_number: c.name,
    }));
  }, [clinicOptionsData]);

  const clinicLocationOptions = useMemo(() => {
    const data = singleClinicData?.data?.data ?? singleClinicData?.data;
    const locations = data?.clinic_locations ?? [];
    return Array.isArray(locations) ? locations : [];
  }, [singleClinicData]);
  const clinicSiteType = useMemo(() => {
    const data = singleClinicData?.data?.data ?? singleClinicData?.data;
    return (data?.site_type ?? null) as string | null;
  }, [singleClinicData]);

  const breadcrumbItems = useMemo(
    () => [
      {
        title: (
          <Link to={`/${siteForBreadcrumb}/admin`} style={{ color: 'inherit' }}>
            Home
          </Link>
        ),
      },
      {
        title:
          clinicIdForLocations && clinicIdForLocations > 0 ? (
            <Link
              to={`/${siteForBreadcrumb}/admin/clinics/${clinicIdForLocations}`}
              style={{ color: 'inherit' }}
            >
              治療院詳細
            </Link>
          ) : (
            '治療院詳細'
          ),
      },
      { title: `編集:${id}` },
    ],
    [siteForBreadcrumb, id, clinicIdForLocations]
  );

  /** BOTH when clinic has both; otherwise A2/R2 comes from massager.service_type. */
  const clinicServiceType = useMemo((): 'A2' | 'R2' | 'BOTH' => {
    const clinicRaw = (singleClinicData?.data?.data ?? singleClinicData?.data)?.service_type;
    const clinicS = (clinicRaw ?? '').toString().toUpperCase();
    if (clinicS === SERVICE_TYPE_BOTH) return SERVICE_TYPE_BOTH;
    const raw = massagerData?.service_type;
    const s = (raw ?? '').toString().toUpperCase();
    if (s === SERVICE_TYPE_R2) return SERVICE_TYPE_R2;
    return SERVICE_TYPE_A2;
  }, [massagerData?.service_type, singleClinicData]);

  const handleSubmit = useCallback(
    (data: CreateMassagerFormData): Promise<void> => {
      if (!site || !massagerId) {
        toast.error('サイトまたは施術師情報が取得できませんでした。');
        return Promise.resolve();
      }
      const wideAreaRowsA2 = (data.wide_area_union_rows ?? []).filter(
        r =>
          (r.wide_area_union_code ?? '').trim() !== '' &&
          (r.wide_area_union_number ?? '').trim() !== ''
      );
      const wideAreaRowsR2 = (data.massage_wide_area_union_rows ?? []).filter(
        r =>
          (r.wide_area_union_code ?? '').trim() !== '' &&
          (r.wide_area_union_number ?? '').trim() !== ''
      );
      const isR2 = clinicServiceType === SERVICE_TYPE_R2;
      const isBoth = clinicServiceType === SERVICE_TYPE_BOTH;
      const toLocationPayload = (
        rows: Array<{
          clinic_location_id?: number | null;
          available_from?: string | null;
          available_to?: string | null;
          regist_date?: string | null;
          stop_request_date?: string | null;
        }>
      ) =>
        rows
          .filter(r => (r?.clinic_location_id ?? 0) > 0)
          .map(r => ({
            id: r.clinic_location_id!,
            available_from: dateValueToIso(r.available_from ?? null),
            available_to: dateValueToIso(r.available_to ?? null),
            regist_date: dateValueToIso(r.regist_date ?? null),
            stop_request_date: dateValueToIso(r.stop_request_date ?? null),
          }));

      const payload: UpdateMassagerPayload = {
        service_type: clinicServiceType,
        name: data.name,
        name_kana: data.name_kana ?? null,
        login_id: data.login_id ?? null,
        password: data.password ?? undefined,
        email: data.email ?? null,
        wide_area_union_rows: wideAreaRowsA2.length ? wideAreaRowsA2 : undefined,
        massage_wide_area_union_rows: (isR2 || isBoth) && wideAreaRowsR2.length ? wideAreaRowsR2 : undefined,
      };

      // Only send clinic_location_ids_* for the active service side (objects: id, available_from, available_to)
      if (!isR2) {
        payload.clinic_location_ids_a2 = toLocationPayload(data.clinic_locations_a2 ?? []);
      }
      if (isR2 || isBoth) {
        payload.clinic_location_ids_r2 = toLocationPayload(data.clinic_locations_r2 ?? []);
      }
      if (isR2 || isBoth) {
        payload.massage_license_number = data.license_number ?? null;
      }
      if (!isR2 || isBoth) {
        payload.acupuncture_license_number = data.acupuncture_license_number ?? null;
        payload.moxibustion_license_number = data.moxibustion_license_number ?? null;
      }
      return updateMassagerMutation({
        site,
        id: massagerId,
        data: payload,
      }).then(() => { });
    },
    [site, massagerId, clinicServiceType, updateMassagerMutation, toast]
  );

  const initialValues = useMemo(
    () => (massagerData ? mapDetailToFormData(massagerData) : undefined),
    [massagerData]
  );

  const lastVisitDate = massagerData?.last_visit_date ?? null;

  if (isLoadingMassager) {
    return <LoadingTemplate />;
  }

  const massagerAxiosError = massagerError as any;
  const isNotFound =
    (isMassagerError && massagerAxiosError?.response?.status === 404) || !massagerData;

  if (isNotFound) {
    return <SiteAdminNotFoundTemplate />;
  }

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} showHome={false} />
      <AppCard title={`施術師編集:${id}`} className="w-full">
        <CreateMassagerForm
          clinicOptions={clinicOptions}
          clinicLocationOptions={clinicLocationOptions}
          initialClinicId={massagerData.clinic_id}
          fixedClinicId={massagerData.clinic_id}
          initialValues={initialValues}
          isEdit
          serialNumber={massagerData.serial_number ?? null}
          clinicServiceType={clinicServiceType}
          siteType={clinicSiteType}
          lastVisitDate={lastVisitDate}
          onClinicIdChange={setSelectedClinicIdForLocations}
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
          isLoading={isLoading}
        />
        <AuditInfo
          createdBy={massagerData?.created_by}
          updatedBy={massagerData?.updated_by}
          updatedAt={massagerData?.updated_at}
        />
      </AppCard>
    </AppContainer>
  );
};

export default EditMassagerPage;
